import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { useAssets } from '../data/fleet.ts'
import { OPEN_WORK_ORDER_STATES, useAvailability, useDefectViews, useWorkOrders } from '../data/safety.ts'
import { ASSET_SECTIONS, machineName } from '../data/workshopUi.ts'
import { formatLocal } from '../lib/time.ts'
import { Pill } from '../ui/components.tsx'
import { RowLink } from '../ui/RowLink.tsx'
import { useWriteAccess } from '../ui/hooks.ts'
import { SEVERITY_TONE } from './SafetyPanel.tsx'

const SEV_ORDER = { critical: 0, major: 1, minor: 2 } as const

/** The workshop's overview: what is out of service and why, which defects are still open, which work orders are in progress. A read of the
 *  device's own data; the actions are on each machine's page, and each card opens that page on its own section (M23 item 33: the orders'
 *  card used to land on the top of a 4 000-px page). Machines are named by make and model, as the mechanic names them, beside their tag. */
export function WorkshopPage() {
  const { t } = useTranslation()
  const { site } = useClub()
  const assets = useAssets()
  const catalogue = useWriteAccess('catalog.read')
  const { blockers, ready } = useAvailability()
  const views = useDefectViews()
  const orders = useWorkOrders()
  const byId = useMemo(() => new Map((assets ?? []).map((a) => [a.id, a])), [assets])
  const out = useMemo(() => (assets ?? []).filter((a) => a.lifecycle !== 'retired' && blockers(a.id).length > 0), [assets, blockers])
  const open = useMemo(() => (views ?? []).filter((v) => v.status === 'open').sort((a, b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity] || (a.defect.reported_at < b.defect.reported_at ? 1 : -1)), [views])
  const live = useMemo(() => (orders ?? []).filter((w) => OPEN_WORK_ORDER_STATES.includes(w.state) || w.state === 'repaired').sort((a, b) => (a.opened_at < b.opened_at ? 1 : -1)), [orders])
  const fmt = (s: string) => formatLocal(s, site.timezone, 'dd/MM/yyyy')
  const name = (id: string) => { const a = byId.get(id); return a ? <><span className="font-bold">{a.readable_tag}</span>{machineName(a) && <span className="font-medium"> · {machineName(a)}</span>}</> : '…' }
  if (!ready || !assets) return <p>{t('common.loading')}</p>
  return (
    <section className="flex flex-col gap-6" data-testid="workshop-page">
      <h1 className="text-2xl font-bold">{t('workshop.title')}</h1>
      <p className="meta">{t('workshop.intro')}</p>

      <section className="flex flex-col gap-3">
        <h2 className="text-xl font-bold">{t('workshop.outTitle', { count: out.length })}</h2>
        {out.length === 0 ? <p className="opacity-70" data-testid="workshop-none-out">{t('workshop.noneOut')}</p> : (
          <ul className="flex flex-col gap-2" data-testid="workshop-out">
            {out.map((a) => (
              <li key={a.id}>
                <Link to="/assets/$assetId" params={{ assetId: a.id }} className="card flex flex-col gap-1 !border-red-400 dark:!border-red-600">
                  <span className="flex flex-wrap items-center justify-between gap-2"><span className="text-lg">{name(a.id)}</span><Pill tone="red">{t('safety.outOfService')}</Pill></span>
                  {blockers(a.id).map((b) => (
                    <span key={b.restriction.id} className="text-sm opacity-85">
                      {t(`safety.reason.${b.restriction.reason_kind}`, { text: (b.restriction.reason_kind === 'critical_defect' ? b.defect?.description : b.restriction.note) || t('safety.reason.noText') })}
                      {!b.own && ` (${t('safety.reason.via', { tag: b.via?.readable_tag ?? '…' })})`}
                    </span>
                  ))}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-xl font-bold">{t('workshop.defectsTitle', { count: open.length })}</h2>
        {open.length === 0 ? <p className="opacity-70">{t('defect.none')}</p> : (
          <ul className="flex flex-col gap-2" data-testid="workshop-defects">
            {open.map((v) => (
              <li key={v.defect.id}>
                <Link to="/assets/$assetId" params={{ assetId: v.defect.asset_id }} hash={ASSET_SECTIONS.safety} className="card flex flex-col gap-1">
                  <span className="flex flex-wrap items-center gap-2"><span>{name(v.defect.asset_id)}</span><Pill tone={SEVERITY_TONE[v.severity]}>{t(`defect.severity.${v.severity}`)}</Pill></span>
                  <span className="text-sm">{v.defect.description}</span>
                  <span className="meta">{t('defect.reportedAt', { when: fmt(v.defect.reported_at) })}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-xl font-bold">{t('workshop.ordersTitle', { count: live.length })}</h2>
        {live.length === 0 ? <p className="opacity-70">{t('workorder.none')}</p> : (
          <ul className="flex flex-col gap-2" data-testid="workshop-orders">
            {live.map((w) => (
              <li key={w.id}>
                <Link to="/assets/$assetId" params={{ assetId: w.asset_id }} hash={ASSET_SECTIONS.workorders} className="card flex flex-col gap-1" data-testid="workshop-order">
                  <span className="flex flex-wrap items-center gap-2"><span>{name(w.asset_id)}</span><Pill tone={w.state === 'repaired' ? 'green' : 'amber'}>{t(`workorder.state.${w.state}`)}</Pill></span>
                  <span className="text-sm">{w.complaint ?? t(`workorder.kind.${w.kind}`)}</span>
                  <span className="meta">{t(`workorder.kind.${w.kind}`)} · {fmt(w.opened_at)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {catalogue.permitted && <RowLink to="/catalogue" icon="box" tone="sand" title={t('workshop.catalogue')} hint={t('workshop.catalogueHint')} testId="workshop-catalogue" />}
    </section>
  )
}
